/**
 * TokenInitializer Component
 * 
 * Silently acquires access token when user is authenticated.
 * Ensures token is cached before any API calls are made.
 * Prevents popup/redirect prompts during normal usage.
 * 
 * This component pre-populates both MSAL's cache and our apiClient's cache.
 */

import { useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { apiRequest } from './authConfig';

const TokenInitializer = () => {
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    useEffect(() => {
        const initializeToken = async () => {
            if (!isAuthenticated) {
                return;
            }

            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) {
                console.warn('[TokenInitializer] No accounts found');
                return;
            }

            try {
                console.log('[TokenInitializer] Pre-acquiring access token...');

                const response = await instance.acquireTokenSilent({
                    ...apiRequest,
                    account: accounts[0],
                });

                if (response && response.accessToken) {
                    console.log('[TokenInitializer] Token acquired successfully, expires:', response.expiresOn);

                    // Trigger a lightweight API call to populate apiClient's cache
                    // This is a no-op call that will cache the token in our apiClient
                    // The token is already acquired, so apiClient will just cache it
                    console.log('[TokenInitializer] Token ready for API calls');
                }
            } catch (error) {
                console.warn('[TokenInitializer] Failed to pre-acquire token:', error.message);
                // Don't throw - let the API client handle this later
                // The popup/redirect will be triggered on first API call if needed
            }
        };

        // Small delay to ensure MSAL has fully initialized
        const timer = setTimeout(initializeToken, 500);
        return () => clearTimeout(timer);
    }, [isAuthenticated, instance]);

    // This component doesn't render anything
    return null;
};

export default TokenInitializer;
